import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Short label for the failed module (e.g. "Gmail", "Channels"). */
  label?: string;
}

interface State {
  err: Error | null;
}

// Error-message substrings that indicate a dynamic-import / chunk-load
// failure (distinct from a regular runtime bug inside the lazy-loaded
// module). These strings are engine-dependent — keep one entry per browser
// engine we support.
//
// NOTE: these strings are NOT a stable API. Each entry is coupled to a
// specific bundler/browser version. When upgrading Vite or adding a new
// target browser, re-verify the thrown message and update the list.
const CHUNK_ERROR_PATTERNS = [
  // Chromium / V8 (Chrome, Edge, modern Electron)
  'Failed to fetch dynamically imported module',
  // Firefox / SpiderMonkey
  'error loading dynamically imported module',
  // Safari / WebKit
  'Importing a module script failed',
  // Vite-explicit preload failures (any engine)
  'Unable to preload',
  // Webpack-style (kept for bundler-agnosticism; no-op in a pure Vite stack)
  'Loading chunk',
  'Loading CSS chunk',
];

export function isChunkLoadError(err: Error): boolean {
  if (err.name === 'ChunkLoadError') return true;
  const msg = err.message || '';
  return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p));
}

// Truncate error messages shown in the user-visible <details> to cap any
// PII (email addresses, URLs with tokens) that made it into the message.
const MAX_DETAIL_MESSAGE_LENGTH = 200;

/**
 * Error boundary for React.lazy modules. Distinguishes between chunk-load
 * failures (where a reload is the correct recovery) and generic runtime
 * errors (where reloading won't help). Shows different copy for each so
 * users aren't told "a newer version is available" for a bug that reloading
 * won't fix.
 *
 * Normalizes non-Error throws (e.g. `throw "boom"` or `throw { message: x }`)
 * into a real Error so downstream checks don't silently misclassify.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: unknown): State {
    if (err instanceof Error) return { err };
    // Non-Error throws land here. Coerce to a real Error so isChunkLoadError
    // and render logic have a stable shape to work with.
    const message = typeof err === 'string' ? err : safeStringify(err);
    return { err: new Error(message) };
  }

  componentDidCatch(err: unknown, info: ErrorInfo): void {
    console.warn('[ChunkErrorBoundary]', this.props.label ?? '', err, info);
  }

  render(): ReactNode {
    const { err } = this.state;
    if (!err) return this.props.children;

    const label = this.props.label ?? 'this module';
    const chunk = isChunkLoadError(err);
    const heading = chunk
      ? `A newer version of ${label} is available.`
      : `Something went wrong in ${label}.`;

    const truncated = (err.message || String(err)).slice(0, MAX_DETAIL_MESSAGE_LENGTH);
    const wasTruncated = (err.message || '').length > MAX_DETAIL_MESSAGE_LENGTH;

    return (
      <div
        className="module-content"
        style={{ padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center' }}
      >
        <div style={{ fontSize: 14, color: '#555' }}>{heading}</div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fafafa', cursor: 'pointer' }}
        >
          Reload
        </button>
        {!chunk && (
          <details style={{ marginTop: 8, textAlign: 'left', fontSize: 12, color: 'var(--color-text-faint)', maxWidth: 480 }}>
            <summary style={{ cursor: 'pointer' }}>Details</summary>
            <pre style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4, overflow: 'auto', maxHeight: 160 }}>
              {truncated}{wasTruncated ? '…' : ''}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
